import { MINT_GAS_LIMIT_MULTIPLIER, MINT_FALLBACK_GAS_LIMIT, NULL_ADDRESS, UINT32_MAX } from '../../../utils/constants.mjs';
import { exhaustiveGuard, scaleAmount, curry } from '../../../utils/helpers.mjs';
import { SOUND_EDITION_V2_ABI } from '../abi/sound-edition-v2.mjs';
import { getSuperMinterForEdition, getTierCurrentMaxMintable } from './helpers.mjs';

async function getTotalMintPriceAndFees(client, { tier, scheduleNum, quantity, editionAddress }) {
  const superMinter = await getSuperMinterForEdition(client, { editionAddress });
  switch (superMinter.version) {
    case "1": {
      const { abi, address } = superMinter;
      return client.readContract({
        abi,
        address,
        functionName: "totalPriceAndFees",
        args: [editionAddress, tier, scheduleNum, quantity]
      }).then((res) => ({ ...res, version: superMinter.version }));
    }
    case "2": {
      const { abi, address } = superMinter;
      return client.readContract({
        abi,
        address,
        functionName: "totalPriceAndFees",
        args: [editionAddress, tier, scheduleNum, quantity, false]
      }).then((res) => ({ ...res, version: superMinter.version }));
    }
    default:
      exhaustiveGuard(superMinter);
  }
}
async function mintEligibility(client, { merkleProvider }, { tier, scheduleNum, collectorAddress, editionAddress }) {
  const { address, abi } = await getSuperMinterForEdition(client, { editionAddress });
  const [numberMintedOnSchedule, scheduleInfo, tierInfo] = await client.multicall({
    contracts: [
      {
        abi,
        address,
        functionName: "numberMinted",
        args: [editionAddress, tier, scheduleNum, collectorAddress]
      },
      {
        abi,
        address,
        functionName: "mintInfo",
        args: [editionAddress, tier, scheduleNum]
      },
      {
        abi: SOUND_EDITION_V2_ABI,
        address: editionAddress,
        functionName: "tierInfo",
        args: [tier]
      }
    ],
    allowFailure: false
  });
  const { paused, mode, maxMintable, minted, maxMintablePerAccount } = scheduleInfo;
  if (paused) {
    return {
      numberMintedOnSchedule,
      remainingEligibility: 0
    };
  }
  const tierCurrentMax = tierInfo.mintConcluded ? 0 : Math.max(getTierCurrentMaxMintable(tierInfo) - tierInfo.minted, 0);
  const availableOnSchedule = maxMintable - minted;
  const remainingPerAccountLimit = maxMintablePerAccount - numberMintedOnSchedule;
  const remainingEligibility = Math.min(availableOnSchedule, remainingPerAccountLimit, tierCurrentMax);
  if (mode === 0) {
    return {
      numberMintedOnSchedule,
      remainingEligibility
    };
  }
  if (mode === 1) {
    const { merkleRoot } = scheduleInfo;
    const proof = await merkleProvider.merkleProof({ merkleRoot, userAddress: collectorAddress });
    if (!proof?.length) {
      return {
        numberMintedOnSchedule,
        remainingEligibility: 0
      };
    }
    return {
      numberMintedOnSchedule,
      remainingEligibility
    };
  } else if (mode === 2) {
    throw new Error("Not implemented");
  } else {
    throw new Error("Unknown minting mode");
  }
}
async function editionMintParameters(client, { merkleProvider }, {
  editionAddress,
  mintTo,
  quantity,
  schedule,
  account,
  gas,
  maxFeePerGas,
  maxPriorityFeePerGas,
  chain,
  affiliate = NULL_ADDRESS,
  affiliateProof = [],
  attributionId = 0n
}) {
  const tier = schedule.tier;
  const eligibility = await mintEligibility(
    client,
    { merkleProvider },
    {
      collectorAddress: mintTo,
      scheduleNum: schedule.scheduleNum,
      tier,
      editionAddress
    }
  );
  if (!eligibility.remainingEligibility) {
    return {
      mint: {
        type: "not-eligible"
      }
    };
  }
  const {
    signedPrice,
    signedQuantity,
    signedClaimTicket,
    signedDeadline,
    signature
  } = { signature: NULL_ADDRESS, signedPrice: 0n, signedQuantity: 0, signedClaimTicket: 0, signedDeadline: 0 };
  const txnOverrides = {
    gas,
    maxFeePerGas,
    maxPriorityFeePerGas
  };
  const allowlistedQuantity = UINT32_MAX;
  const { total: value } = await getTotalMintPriceAndFees(client, {
    tier,
    quantity,
    scheduleNum: schedule.scheduleNum,
    editionAddress
  });
  const superMinter = await getSuperMinterForEdition(client, { editionAddress });
  const sharedWriteContractParameters = {
    address: superMinter.address,
    abi: superMinter.abi,
    functionName: "mintTo",
    account,
    value,
    chain
  };
  switch (schedule.mode) {
    case "DEFAULT": {
      const args = [
        {
          edition: editionAddress,
          tier,
          scheduleNum: schedule.scheduleNum,
          to: mintTo,
          quantity,
          allowlisted: NULL_ADDRESS,
          allowlistedQuantity,
          allowlistProof: [],
          signedPrice,
          signedQuantity,
          signedClaimTicket,
          signedDeadline,
          signature,
          affiliate,
          affiliateProof,
          attributionId
        }
      ];
      let gasEstimate;
      try {
        gasEstimate = txnOverrides.gas = scaleAmount({
          amount: await client.estimateContractGas({
            args,
            ...sharedWriteContractParameters,
            ...txnOverrides
          }),
          multiplier: MINT_GAS_LIMIT_MULTIPLIER
        });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
        gasEstimate = null;
      }
      return {
        mint: {
          type: "mint",
          input: {
            args,
            ...sharedWriteContractParameters,
            ...txnOverrides
          },
          gasEstimate
        }
      };
    }
    case "VERIFY_MERKLE": {
      const proof = await merkleProvider.merkleProof({ merkleRoot: schedule.merkleRoot, userAddress: mintTo });
      if (!proof?.length) {
        return {
          mint: {
            type: "not-eligible"
          }
        };
      }
      const args = [
        {
          edition: editionAddress,
          tier,
          scheduleNum: schedule.scheduleNum,
          to: mintTo,
          quantity,
          allowlisted: mintTo,
          allowlistedQuantity,
          allowlistProof: proof,
          signedPrice,
          signedQuantity,
          signedClaimTicket,
          signedDeadline,
          signature,
          affiliate,
          affiliateProof,
          attributionId
        }
      ];
      let gasEstimate;
      try {
        gasEstimate = txnOverrides.gas = scaleAmount({
          amount: await client.estimateContractGas({
            args,
            ...sharedWriteContractParameters,
            ...txnOverrides
          }),
          multiplier: MINT_GAS_LIMIT_MULTIPLIER
        });
      } catch (err) {
        txnOverrides.gas = MINT_FALLBACK_GAS_LIMIT;
        gasEstimate = null;
      }
      return {
        mint: {
          type: "mint",
          input: {
            args,
            ...sharedWriteContractParameters,
            ...txnOverrides
          },
          gasEstimate
        }
      };
    }
    case "VERIFY_SIGNATURE":
    default: {
      throw Error("Not implemented");
    }
  }
}
function editionV2PublicActionsMint(client) {
  return {
    editionV2: {
      ...client.editionV2,
      totalMintPriceAndFees: curry(getTotalMintPriceAndFees)(client),
      eligibility: curry(mintEligibility)(client)({ merkleProvider: client.merkleProvider }),
      mintParameters: curry(editionMintParameters)(client)({ merkleProvider: client.merkleProvider })
    }
  };
}

export { editionMintParameters, editionV2PublicActionsMint, getTotalMintPriceAndFees, mintEligibility };
